import { AiBox, FormSection } from '@percent/domain/giving'
import { MultiSelect, Text } from '@mantine/core'
import {
  FakeCheckbox,
  Flex,
  FormField,
  Icon,
  type IconProps,
  Loader,
} from '@percent/lemonade'
import { useMemo, useState } from 'react'

import type { OpportunityFormSkillsAndActivitiesSectionProps } from './opportunity-form-skills-and-activities-section.types'

export function OpportunityFormSkillsAndActivitiesSection({
  skillsValue,
  onSkillsChange,

  activitiesValue,
  onActivitiesChange,
  activitiesTouched,
  setActivitiesTouched,
  activitiesError,

  skillsData,
  areSkillsLoading,
  activitiesData,
  areActivitiesLoading,
  aiGenerationStatus,

  sectionTitle,
  aiGenerationLabelA,
  aiGenerationLabelB,
  skillsLabel,
  skillsPlaceholder,
  activitiesLabel,
  activitiesPlaceholder,
}: Readonly<OpportunityFormSkillsAndActivitiesSectionProps>) {
  const [selectIcon, setSelectIcon] = useState<{
    activitiesDropdownIcon: IconProps['name']
    skillsDropdownIcon: IconProps['name']
  }>({
    activitiesDropdownIcon: 'dropdown-arrow-down',
    skillsDropdownIcon: 'dropdown-arrow-down',
  })

  const skillsOptions = useMemo(
    () =>
      (skillsData ?? [])
        .map((skill) => ({
          value: skill.id,
          label: skill.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [skillsData]
  )

  const activityTagsOptions = useMemo(
    () =>
      (activitiesData ?? [])
        .map((skill) => ({
          value: skill.id,
          label: skill.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activitiesData]
  )

  return (
    <FormSection title={sectionTitle}>
      {aiGenerationStatus === 'success' && (
        <AiBox withIcon>
          <Text>
            {aiGenerationLabelA}
            <br />
            {aiGenerationLabelB}
          </Text>
        </AiBox>
      )}

      {areActivitiesLoading || areSkillsLoading ? (
        <Loader />
      ) : (
        <>
          <FormField
            label={activitiesLabel}
            necessity="required"
            data-testid="activitiesField"
            status={activitiesTouched && activitiesError ? 'danger' : 'default'}
            statusMessage={activitiesError}
          >
            <MultiSelect
              wrapperProps={{
                'data-error': !!activitiesTouched && !!activitiesError,
              }}
              style={{ width: '100%' }}
              placeholder={
                activitiesValue && activitiesValue.length
                  ? ''
                  : activitiesPlaceholder
              }
              searchable
              data={activityTagsOptions}
              value={activitiesValue}
              onChange={(selectedValues) => onActivitiesChange(selectedValues)}
              rightSection={
                <Icon
                  name={selectIcon.activitiesDropdownIcon}
                  size={8}
                  color="gray600"
                />
              }
              onDropdownOpen={() => {
                setActivitiesTouched()
                setSelectIcon({
                  ...selectIcon,
                  activitiesDropdownIcon: 'dropdown-arrow-up',
                })
              }}
              onDropdownClose={() => {
                setSelectIcon({
                  ...selectIcon,
                  activitiesDropdownIcon: 'dropdown-arrow-down',
                })
              }}
              renderOption={(item) => (
                <Flex justify="center" align="center">
                  <FakeCheckbox isSelected={!!item.checked} />
                  <div style={{ marginLeft: '0.5rem' }}>
                    {item.option.label}
                  </div>
                </Flex>
              )}
            />
          </FormField>

          <FormField
            label={skillsLabel}
            necessity="optional"
            data-testid="skillsField"
          >
            <MultiSelect
              style={{ width: '100%' }}
              placeholder={
                skillsValue && skillsValue.length ? '' : skillsPlaceholder
              }
              searchable
              data={skillsOptions}
              value={skillsValue}
              onChange={(selectedValues) => onSkillsChange(selectedValues)}
              rightSection={
                <Icon
                  name={selectIcon.skillsDropdownIcon}
                  size={8}
                  color="gray600"
                />
              }
              onDropdownOpen={() => {
                setSelectIcon({
                  ...selectIcon,
                  skillsDropdownIcon: 'dropdown-arrow-up',
                })
              }}
              onDropdownClose={() => {
                setSelectIcon({
                  ...selectIcon,
                  skillsDropdownIcon: 'dropdown-arrow-down',
                })
              }}
              renderOption={(item) => (
                <Flex justify="center" align="center">
                  <FakeCheckbox isSelected={!!item.checked} />
                  <div style={{ marginLeft: '0.5rem' }}>
                    {item.option.label}
                  </div>
                </Flex>
              )}
            />
          </FormField>
        </>
      )}
    </FormSection>
  )
}
